import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FolderPlus, X } from 'lucide-react';
import { useCreateKbProject } from '../../hooks/useKbProject';
import { useClients } from '../../hooks/useClients';
import { useTaskTemplates } from '../../hooks/useTaskTemplates';
import type { ProjectHealth, ProjectStatus } from '../../lib/projectsApi';
import type { BudgetCurrency } from '../../lib/kbProjectsApi';
import { Select } from '../ui/Select';
import { DateField } from '../ui/DateField';
import { PROJECT_HEALTHS, PROJECT_STATUSES, healthLabel, statusLabel } from './ProjectBadges';

const BUDGET_CURRENCIES: BudgetCurrency[] = ['RON', 'EUR'];

export interface CreateProjectModalProps {
  open: boolean;
  onClose: () => void;
}

/** Format a numeric string with thousand separators while typing. */
function formatThousands(raw: string): string {
  const cleaned = raw.replace(/[^\d.]/g, '');
  const firstDot = cleaned.indexOf('.');
  let intPart = firstDot === -1 ? cleaned : cleaned.slice(0, firstDot);
  const decPart = firstDot === -1 ? '' : cleaned.slice(firstDot + 1).replace(/\./g, '');
  intPart = intPart.replace(/^0+(?=\d)/, '');
  const withSep = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return firstDot === -1 ? withSep : `${withSep}.${decPart}`;
}

function parseNumber(formatted: string): number | null {
  const n = Number(formatted.replace(/,/g, ''));
  return Number.isFinite(n) && formatted.trim() !== '' ? n : null;
}

/**
 * CLE-223 — manual Create Project modal. Projects stay markdown-first (CLE-41):
 * the backend writes the README.md then syncs the DB, so this never bypasses the
 * "DB is derived" invariant. Mirrors the Create Lead modal's design system
 * (`.ui-modal-*`/`.ui-input`/`.ui-btn-*`), 2-column layout on desktop.
 *
 * Known limitation: team-member assignment isn't wired here — the sync engine
 * derives membership from the README team table, which the AI assistant manages.
 */
export function CreateProjectModal({ open, onClose }: CreateProjectModalProps) {
  const navigate = useNavigate();
  const create = useCreateKbProject();
  const clientsQuery = useClients({ active: 'true', limit: 100 });
  const clients = useMemo(() => clientsQuery.data?.data ?? [], [clientsQuery.data]);
  // Task templates (CLE-DASH): optionally instantiate a template's tasks in the
  // new project (the backend applies it after the README sync).
  const { data: templates } = useTaskTemplates();

  const nameRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<ProjectStatus>('PLANNING');
  const [health, setHealth] = useState<ProjectHealth>('ON_TRACK');
  const [clientId, setClientId] = useState('');
  const [taskTemplateId, setTaskTemplateId] = useState('');
  const [budgetInput, setBudgetInput] = useState('');
  const [budgetCurrency, setBudgetCurrency] = useState<BudgetCurrency>('RON');
  const [isBarter, setIsBarter] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Reset to defaults whenever the modal (re)opens.
  useEffect(() => {
    if (!open) return;
    setName('');
    setCode('');
    setDescription('');
    setStatus('PLANNING');
    setHealth('ON_TRACK');
    setClientId('');
    setTaskTemplateId('');
    setBudgetInput('');
    setBudgetCurrency('RON');
    setIsBarter(false);
    setStartDate('');
    setEndDate('');
    setErrors({});
    setSubmitError(null);
  }, [open]);

  // Autofocus the name input on open.
  useEffect(() => {
    if (open) {
      const t = setTimeout(() => nameRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Escape closes the modal.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  function validate(): boolean {
    const next: Record<string, string> = {};
    const trimmed = name.trim();
    if (!trimmed) next.name = 'Numele proiectului este obligatoriu';
    else if (trimmed.length > 255) next.name = 'Numele trebuie să aibă cel mult 255 de caractere';
    const parsedBudget = parseNumber(budgetInput);
    if (budgetInput.trim() && (parsedBudget === null || parsedBudget < 0))
      next.budget = 'Bugetul trebuie să fie un număr pozitiv';
    if (startDate && endDate && startDate > endDate)
      next.endDate = 'Data de final trebuie să fie egală sau ulterioară datei de început';
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  const isValid = name.trim().length > 0 && name.trim().length <= 255;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    if (create.isPending) return; // double-submit guard
    if (!validate()) return;

    create.mutate(
      {
        name: name.trim(),
        code: code.trim() ? code.trim() : undefined,
        description: description.trim() || null,
        status,
        health,
        clientId: clientId || null,
        taskTemplateId: taskTemplateId || null,
        budget: parseNumber(budgetInput),
        budgetCurrency,
        isBarter,
        startDate: startDate || null,
        endDate: endDate || null,
      },
      {
        onSuccess: (project) => {
          onClose();
          // Navigate to the new project's detail page (prefer code, fall back to id).
          const target = project.code ?? project.id;
          if (target) navigate(`/projects/${encodeURIComponent(target)}`);
        },
        onError: (err) => {
          setSubmitError(err instanceof Error ? err.message : 'Crearea proiectului a eșuat');
        },
      },
    );
  }

  // Enter submits unless focus is in a textarea (or on a button).
  function handleFormKeyDown(e: React.KeyboardEvent) {
    if (e.key !== 'Enter') return;
    const target = e.target as HTMLElement;
    if (target.tagName === 'TEXTAREA') return;
    if (target.tagName === 'BUTTON') return;
    e.preventDefault();
    handleSubmit(e as unknown as React.FormEvent);
  }

  const errText = (key: string) =>
    errors[key] ? <p className="mt-1 text-sm text-red-600">{errors[key]}</p> : null;

  return (
    <div
      className="ui-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-project-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="ui-modal-panel w-full max-w-[640px] max-h-[90vh] p-0">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2
            id="create-project-title"
            className="flex items-center gap-2 text-lg font-semibold text-gray-900"
          >
            <FolderPlus size={18} className="text-brand-600" />
            Creează proiect
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
            aria-label="Închide"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} onKeyDown={handleFormKeyDown}>
          <div className="max-h-[calc(90vh-9rem)] overflow-y-auto px-5 py-4">
            {/* Name */}
            <label className="block text-sm font-medium text-gray-700">
              Nume <span className="text-red-500">*</span>
              <input
                ref={nameRef}
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={255}
                placeholder="ex.: TechCorp — Redesign website"
                className="ui-input mt-1 text-base"
                aria-invalid={Boolean(errors.name)}
              />
            </label>
            {errText('name')}

            {/* Description */}
            <label className="mt-4 block text-sm font-medium text-gray-700">
              Descriere
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="Descriere scurtă a proiectului..."
                className="ui-textarea mt-1 resize-y"
              />
            </label>
            <p className="mt-1 text-xs text-gray-400">Acceptă markdown</p>

            {/* Two columns */}
            <div className="mt-5 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
              {/* ---- Left column: Project Details ---- */}
              <div className="flex flex-col gap-4">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                  Detalii proiect
                </h3>

                {/* Code */}
                <label className="block text-sm font-medium text-gray-700">
                  Cod
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    placeholder="Generat automat dacă e gol"
                    className="ui-input mt-1 font-mono"
                  />
                </label>

                {/* Status */}
                <Select
                  label="Status"
                  value={status}
                  onChange={(v) => setStatus(v as ProjectStatus)}
                  options={PROJECT_STATUSES.map((s) => ({ value: s, label: statusLabel(s) }))}
                />

                {/* Health */}
                <Select
                  label="Sănătate"
                  value={health}
                  onChange={(v) => setHealth(v as ProjectHealth)}
                  options={PROJECT_HEALTHS.map((h) => ({ value: h, label: healthLabel(h) }))}
                />

                {/* Client */}
                <Select
                  label="Client (opțional)"
                  clearable
                  searchable
                  value={clientId}
                  onChange={setClientId}
                  placeholder={clients.length === 0 ? 'Încă niciun client' : 'Fără client'}
                  options={clients.map((c) => ({ value: c.id, label: c.name }))}
                />

                {/* Task template (CLE-DASH) */}
                <Select
                  label="Șablon de sarcini (opțional)"
                  clearable
                  searchable
                  value={taskTemplateId}
                  onChange={setTaskTemplateId}
                  placeholder="Fără șablon"
                  options={(templates ?? []).map((tpl) => ({
                    value: tpl.id,
                    label: tpl.name,
                    description: `${tpl.itemCount} sarcini`,
                  }))}
                />
              </div>

              {/* ---- Right column: Budget + Timeline ---- */}
              <div className="flex flex-col gap-4">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                  Buget &amp; Cronologie
                </h3>

                {/* Budget + Currency */}
                <div className="flex gap-2">
                  <label className="flex-1 text-sm font-medium text-gray-700">
                    Buget
                    <input
                      type="text"
                      inputMode="decimal"
                      value={budgetInput}
                      onChange={(e) => setBudgetInput(formatThousands(e.target.value))}
                      placeholder="0.00"
                      className="ui-input mt-1"
                      aria-invalid={Boolean(errors.budget)}
                    />
                  </label>
                  <div className="w-24 text-sm font-medium text-gray-700">
                    Monedă
                    <Select
                      label="Monedă"
                      hideLabel
                      value={budgetCurrency}
                      onChange={(v) => setBudgetCurrency(v as BudgetCurrency)}
                      options={BUDGET_CURRENCIES.map((c) => ({ value: c, label: c }))}
                      className="mt-1"
                    />
                  </div>
                </div>
                {errText('budget')}

                {/* Barter */}
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <input
                    type="checkbox"
                    checked={isBarter}
                    onChange={(e) => setIsBarter(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                  />
                  Proiect barter (fără buget cash)
                </label>

                {/* Start Date */}
                <label className="block text-sm font-medium text-gray-700">
                  Data de început
                  <DateField value={startDate} onChange={setStartDate} className="mt-1 w-full" />
                </label>

                {/* End Date */}
                <label className="block text-sm font-medium text-gray-700">
                  Data de final
                  <DateField
                    value={endDate}
                    onChange={setEndDate}
                    aria-invalid={Boolean(errors.endDate)}
                    className="mt-1 w-full"
                  />
                </label>
                {errText('endDate')}
              </div>
            </div>

            {submitError && <p className="mt-4 text-sm text-red-600">{submitError}</p>}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-2 border-t border-border px-5 py-4">
            <button type="button" onClick={onClose} className="ui-btn-secondary">
              Anulează
            </button>
            <button
              type="submit"
              disabled={!isValid || create.isPending}
              className="ui-btn-primary"
            >
              {create.isPending ? 'Se creează…' : 'Creează proiect'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
